import {
  McpServerSourceType,
  type PolicyException,
} from '@kirin/api-client/contracts/policy-exceptions';
import { uuidV4 } from '@/lib/uuid';
import type { AllowBlockPolicyKey, PolicyRule } from '../types';

/** Wire `type` for MCP `rule_object` — same enum as `mcpServerSourcesRuleSchema`. */
export type McpServerSourceWireType = McpServerSourceType;

/**
 * MCP serialized rule rows — same keys as `mcpServerSourcesRuleSchema` / policy_exceptions
 * (`name`, `type`). Legacy `rule_object` payloads may still use `mcpNameOrId` / `transport`; readers accept both.
 */
export interface McpRuleObject {
  name?: string;
  version?: string;
  publisher?: string;
  type?: McpServerSourceWireType;
}

/** Legacy shape in older stored payloads */
type McpRuleObjectLegacy = McpRuleObject & {
  mcpNameOrId?: string;
  transport?: string;
};

/**
 * Extensions serialized rule rows — same keys as `extensionsManagementRuleSchema` (`name`).
 * Legacy `rule_object` payloads may still use `extensionId`.
 */
export interface ExtensionsRuleObject {
  name?: string;
  publisher?: string;
  version?: string;
}

type ExtensionsRuleObjectLegacy = ExtensionsRuleObject & {
  extensionId?: string;
};

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function clean(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function mcpWireTypeFromUiTransport(
  raw: string | undefined,
  fallback: McpServerSourceType
): McpServerSourceType {
  const t = raw?.toLowerCase();
  if (
    t === McpServerSourceType.Local ||
    t === McpServerSourceType.Remote ||
    t === McpServerSourceType.Any
  ) {
    return t;
  }
  return fallback;
}

function mcpWireTypeOptionalFromUiTransport(
  raw: string | undefined
): McpServerSourceType | undefined {
  const t = raw?.toLowerCase();
  if (
    t === McpServerSourceType.Local ||
    t === McpServerSourceType.Remote ||
    t === McpServerSourceType.Any
  ) {
    return t;
  }
  return undefined;
}

function emptyFieldsFromColumnIds(columnIds: string[]): Record<string, string> {
  return Object.fromEntries(columnIds.map(id => [id, '']));
}

export function createEmptyRuleRow(policyKey: AllowBlockPolicyKey): PolicyRule {
  if (policyKey === 'mcp-server-sources') {
    return {
      id: uuidV4(),
      fields: {
        mcpNameOrId: '',
        publisher: '',
        transport: McpServerSourceType.Any,
      },
    };
  }
  return {
    id: uuidV4(),
    fields: emptyFieldsFromColumnIds(['extensionId', 'version', 'publisher']),
  };
}

export function policyRuleToMcpRuleObject(rule: PolicyRule): McpRuleObject {
  const type = mcpWireTypeFromUiTransport(
    rule.fields.transport,
    McpServerSourceType.Any
  );
  const name = clean(rule.fields.mcpNameOrId);
  // version intentionally not serialized — field removed from MCP UI
  const publisher = clean(rule.fields.publisher);
  return {
    ...(name !== undefined && { name }),
    ...(publisher !== undefined && { publisher }),
    type,
  };
}

export function mcpRuleObjectToPolicyRule(
  obj: McpRuleObject,
  id: string
): PolicyRule {
  const legacy = obj as McpRuleObjectLegacy;
  const name = legacy.name ?? legacy.mcpNameOrId ?? '';
  const rawType = legacy.type ?? legacy.transport;
  const transport = mcpWireTypeFromUiTransport(
    rawType,
    McpServerSourceType.Any
  );
  return {
    id,
    fields: {
      mcpNameOrId: name,
      // version intentionally omitted — removed from MCP UI (mcp.json has no version field)
      publisher: obj.publisher ?? '',
      transport,
    },
  };
}

export function policyRuleToExtensionsRuleObject(
  rule: PolicyRule
): ExtensionsRuleObject {
  const name = clean(rule.fields.extensionId);
  const version = clean(rule.fields.version);
  const publisher = clean(rule.fields.publisher);
  return {
    ...(name !== undefined && { name }),
    ...(version !== undefined && { version }),
    ...(publisher !== undefined && { publisher }),
  };
}

export function extensionsRuleObjectToPolicyRule(
  obj: ExtensionsRuleObject,
  id: string
): PolicyRule {
  const legacy = obj as ExtensionsRuleObjectLegacy;
  return {
    id,
    fields: {
      extensionId: legacy.name ?? legacy.extensionId ?? '',
      version: obj.version ?? '',
      publisher: obj.publisher ?? '',
    },
  };
}

function activeRules(list: PolicyRule[]): PolicyRule[] {
  return list.filter(r => !r.pendingDelete);
}

export function serializeAllowBlockState(
  policyKey: AllowBlockPolicyKey,
  allowed: PolicyRule[],
  blocked: PolicyRule[]
): { allowed: unknown[]; blocked: unknown[] } {
  const allowedActive = activeRules(allowed);
  const blockedActive = activeRules(blocked);
  if (policyKey === 'mcp-server-sources') {
    return {
      allowed: allowedActive.map(r => policyRuleToMcpRuleObject(r)),
      blocked: blockedActive.map(r => policyRuleToMcpRuleObject(r)),
    };
  }
  return {
    allowed: allowedActive.map(r => policyRuleToExtensionsRuleObject(r)),
    blocked: blockedActive.map(r => policyRuleToExtensionsRuleObject(r)),
  };
}

export function policyRuleToExceptionRuleObject(
  policyKey: AllowBlockPolicyKey,
  rule: PolicyRule
): Record<string, string> {
  if (policyKey === 'mcp-server-sources') {
    const name = clean(rule.fields.mcpNameOrId);
    // version intentionally omitted — field removed from MCP UI (mcp.json has no version field)
    const publisher = clean(rule.fields.publisher);
    const rawType = clean(rule.fields.transport);
    const type = mcpWireTypeOptionalFromUiTransport(rawType);
    return {
      ...(name !== undefined && { name }),
      ...(publisher !== undefined && { publisher }),
      ...(type !== undefined && { type }),
    };
  }
  const name = clean(rule.fields.extensionId);
  const version = clean(rule.fields.version);
  const publisher = clean(rule.fields.publisher);
  return {
    ...(name !== undefined && { name }),
    ...(version !== undefined && { version }),
    ...(publisher !== undefined && { publisher }),
  };
}

export function policyExceptionToPolicyRule(
  policyKey: AllowBlockPolicyKey,
  exception: PolicyException
): PolicyRule {
  const obj = exception.ruleObject;
  if (policyKey === 'mcp-server-sources') {
    const typeRaw = asString(obj.type);
    const transport = mcpWireTypeFromUiTransport(
      typeRaw,
      McpServerSourceType.Any
    );
    return {
      id: exception.id,
      fields: {
        mcpNameOrId: asString(obj.name) ?? '',
        // version intentionally omitted — removed from MCP UI (mcp.json has no version field)
        publisher: asString(obj.publisher) ?? '',
        transport,
      },
    };
  }
  return {
    id: exception.id,
    fields: {
      extensionId: asString(obj.name) ?? '',
      version: asString(obj.version) ?? '',
      publisher: asString(obj.publisher) ?? '',
    },
  };
}
